/**
 * Compute recent-note views for AddTxScreen's `常用備註` chip row.
 *
 * Sharp edge: OCR-sourced rows (ReceiptScanScreen) write notes shaped as
 *   `${merchant}\n${itemBullets}` — multi-line, often very long. These make
 *   terrible re-tap chips, so we exclude any note that contains a newline.
 *   AddTxScreen's note `<input>` is single-line so user-typed values can
 *   never contain `\n`; the heuristic is exact for current call sites.
 *
 * `buildRecentNotesIndex` produces three views in a single sort+pass:
 *   - bySubcategory: per-subcategory top 6 unique notes (newest first)
 *   - global: top 6 unique notes across the whole input (newest first)
 *   - allUnique: ALL unique notes, no cap, newest first (used by the
 *     AddTx typing-mode search pool)
 *
 * `recentNotesByCategory` is retained as a deprecated alias whose return
 * value equals `bySubcategory`. The historic name is misleading — the
 * keys are subcategory ids, not category ids — but the function name is
 * kept to avoid breaking external callers in the transition window.
 */
import type { TransactionRow } from './sheets/types';

const MAX_PER_SUBCATEGORY = 6;
const MAX_GLOBAL = 6;

export interface RecentNotesIndex {
  bySubcategory: Readonly<Record<string, ReadonlyArray<string>>>;
  global: ReadonlyArray<string>;
  allUnique: ReadonlyArray<string>;
}

export function buildRecentNotesIndex(
  transactions: ReadonlyArray<TransactionRow>,
): RecentNotesIndex {
  const sorted = [...transactions].sort((a, b) => {
    const byDate = b.date.localeCompare(a.date);
    if (byDate !== 0) return byDate;
    return b.created_at.localeCompare(a.created_at);
  });

  const bySubcategory: Record<string, string[]> = {};
  const global: string[] = [];
  const globalSeen = new Set<string>();
  const allUnique: string[] = [];
  const allSeen = new Set<string>();

  for (const t of sorted) {
    if (t.deleted_at) continue;
    const note = t.note;
    if (!note) continue;
    if (note.includes('\n')) continue;

    if (!allSeen.has(note)) {
      allSeen.add(note);
      allUnique.push(note);
    }

    if (!globalSeen.has(note) && global.length < MAX_GLOBAL) {
      globalSeen.add(note);
      global.push(note);
    }

    const subId = t.subcategory_id;
    if (subId) {
      const bucket = bySubcategory[subId] ?? [];
      if (bucket.length < MAX_PER_SUBCATEGORY && !bucket.includes(note)) {
        bucket.push(note);
        bySubcategory[subId] = bucket;
      }
    }
  }

  return { bySubcategory, global, allUnique };
}

/**
 * @deprecated Use `buildRecentNotesIndex` and read `bySubcategory` instead.
 * The original name implied keying by main category, but the implementation
 * now (and going forward) keys by subcategory id.
 */
export function recentNotesByCategory(
  transactions: ReadonlyArray<TransactionRow>,
): Record<string, string[]> {
  const index = buildRecentNotesIndex(transactions);
  // Return a fresh mutable shape so callers that mutate the result (legacy
  // usage) don't accidentally mutate frozen state.
  const out: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(index.bySubcategory)) {
    out[k] = [...v];
  }
  return out;
}
